import {
  Button,
  ListView,
  ListItem,
  Select,
  IconButton,
  FormField,
  Icon,
} from '@dev-spendesk/grapes';
import React, { type Dispatch, useEffect } from 'react';

import {
  type SubfilterValueWithId,
  type Field,
} from 'src/core/common/components/FilterBuilder';
import { type FilterBuilderAction } from 'src/core/common/components/FilterBuilder/FilterBuilderForm';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { type Condition } from './utils/validateConditions';
import { type Output } from './utils/validateOutputs';

type SubfilterOutputsValueWithId = Omit<SubfilterValueWithId, 'operator'> & {
  valueError?: string;
};
export type FilterBuilderOutputAction = Omit<
  FilterBuilderAction,
  'subfilterValue'
> & {
  subfilterValue: SubfilterOutputsValueWithId & {
    valueError?: string;
  };
  subfilterGroupId: string; // Add subfilterGroupId to the type
};
type Props = {
  fields: Field[];
  outputs: Output[];
  conditions: Condition[];
  onOutputsChange: (updatedOutputs: Output[]) => void;
  dispatchOutputAction: Dispatch<FilterBuilderOutputAction>;
  renderSubfilterValue(params: {
    subfilterValue: SubfilterOutputsValueWithId;
    updateSubfilterValue(newSubfilterValue: SubfilterOutputsValueWithId): void;
    subfilterOperatorName: string;
    subfilterValueName: string;
  }): React.ReactNode;
  outputFieldName: string;
  outputValueName: string;
};
export const OutputsEditor = ({
  fields = [],
  outputs = [],
  conditions,
  onOutputsChange,
  dispatchOutputAction,
  renderSubfilterValue,
  outputFieldName,
  outputValueName,
}: Props) => {
  const { t } = useTranslation('global');
  useEffect(() => {
    if (outputs.length === 0) {
      onOutputsChange([{ field: '', value: '' }]); // Initialize with one empty output
    }
  }, [outputs]);
  const isOutputComplete = (output: Output) => output.field && output.value;

  const handleFieldChange = (index: number, value: string) => {
    const updatedOutputs = [...outputs];
    updatedOutputs[index] = {
      ...updatedOutputs[index],
      field: value,
      value: '',
    };

    // Fetch options for the selected field
    const fieldOptions =
      fields.find((field) => field.key === value)?.options || [];
    updatedOutputs[index].availableOptions = fieldOptions;

    onOutputsChange(updatedOutputs);
  };

  const handleDeleteOutput = (index: number) => {
    if (outputs.length === 1) {
      onOutputsChange([{ field: '', value: '' }]);
      return;
    }
    const updatedOutputs = outputs.filter(
      (_, currentIndex) => currentIndex !== index,
    );
    onOutputsChange(updatedOutputs);
  };

  const handleAddOutput = () => {
    const updatedOutputs = [...outputs, { field: '', value: '' }];
    onOutputsChange(updatedOutputs);
  };

  const getAvailableFieldOptions = () => {
    const selectedFields = outputs.map((output) => output.field);
    const selectedConditionFields = conditions.map((condition) => {
      return condition.field;
    });
    return fields
      .flatMap((field) => field.options ?? [])
      .filter((option) => !selectedConditionFields.includes(option.key))
      .map((option) => ({
        key: option.key,
        label: option.label,
        value: option.key,
        disabled: selectedFields.includes(option.key),
      }));
  };

  const getErrorMessages = () => {
    const fieldErrors = outputs.filter(
      (output) => output.fieldError || output.valueError,
    );
    if (fieldErrors.length === 1) {
      return t(
        'bookkeep.integrations.settings.payableRules.errors.outputs.general',
      );
    }
    return t('bookkeep.integrations.settings.payableRules.errors.multiError');
  };

  return (
    <div className="mb-8 w-full">
      <ListView aria-label="Outputs List" className="outputs-list">
        {outputs.map((output, index) => (
          <ListItem
            key={output.field || `output-${index}`}
            className={`output-list-item mb-8 w-full rounded-12 bg-background-secondary-default pb-16 pl-16 pr-8 pt-16 ${output.fieldError || output.valueError ? 'error-bg' : ''}`}
          >
            <div className="flex w-full items-center gap-8">
              <FormField
                label="Field"
                visuallyHideLabel
                className="w-full"
                alertMessage={output.fieldError}
              >
                <Select
                  className="w-full"
                  placeholder={t(
                    'bookkeep.integrations.settings.payableRules.outputs.placeholder',
                  )}
                  name={`${outputFieldName}-${output.field}`}
                  options={getAvailableFieldOptions()}
                  value={
                    getAvailableFieldOptions().find(
                      (option) => option.value === output.field,
                    ) || undefined
                  }
                  dropdownContentMaxHeight="450px"
                  onSelect={(selected) =>
                    handleFieldChange(index, selected.value)
                  }
                />
              </FormField>

              {output.field && (
                <>
                  <div>
                    {t(
                      'bookkeep.integrations.settings.payableRules.outputs.is',
                    )}
                  </div>

                  {renderSubfilterValue({
                    subfilterValue: {
                      id: `${index}`,
                      field: output.field,
                      value: output.value,
                      valueError: output.valueError,
                    },
                    updateSubfilterValue: (newSubfilterValue) => {
                      const updatedOutputs = [...outputs];
                      updatedOutputs[index] = {
                        field: newSubfilterValue.field || '',
                        value: newSubfilterValue.value as string,
                      };
                      onOutputsChange(updatedOutputs);
                      dispatchOutputAction({
                        type: 'updateSubfilterValue',
                        subfilterGroupId: `group-${index}`,
                        subfilterValue: newSubfilterValue,
                      });
                    },
                    subfilterOperatorName: ``,
                    subfilterValueName: `${outputValueName}-${output.field}`,
                  })}
                </>
              )}
              <IconButton
                className="my-auto mr-0 shrink-0"
                hasNegativeMargins
                name="trash"
                iconName="trash"
                variant="tertiaryNeutral"
                aria-label={`Delete output ${index}`}
                onClick={() => handleDeleteOutput(index)}
              />
            </div>
          </ListItem>
        ))}
      </ListView>
      {outputs.some((output) => output.fieldError || output.valueError) && (
        <div className="error-messages text-danger-default mb-8 mt-4">
          <output className="_grapes-lb" data-severity="alert">
            <Icon size="m" name="hexagone-cross" />
            <span>{getErrorMessages()}</span>
          </output>
        </div>
      )}
      {outputs.some(isOutputComplete) &&
        outputs.length < getAvailableFieldOptions().length && ( // Render button only if at least one output is complete
          <Button
            variant="secondaryNeutral"
            onClick={handleAddOutput}
            iconName="plus"
            text={t('bookkeep.integrations.settings.payableRules.outputs.add')}
            className="w-full"
          />
        )}
    </div>
  );
};
